'use client'
import Link from 'next/link'
import { Logo } from "@/components/marketing/2026/navbar/Logo"
import { Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import React from 'react'
import { cn } from '@/lib/utils'
import { useTranslations } from "next-intl"
import { useLanguage } from "@/lib/LanguageContext"
import { ModeToggle } from "@/components/mode-toggle"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Globe } from "lucide-react"

import { getAuthUrl } from '@/lib/auth-urls'

export const HeroHeader = () => {
    const [menuState, setMenuState] = React.useState(false)
    const [isScrolled, setIsScrolled] = React.useState(false)
    const t = useTranslations('marketing.navbar')
    const { language, setLanguage, dir } = useLanguage()

    const menuItems = [
        { name: t('features'), href: `/${language}/features` },
        { name: t('pricing'), href: `/${language}/pricing` },
        { name: t('blog') || "Blog", href: `/${language}/blog` },
        { name: t('contact') || 'Contact', href: `/${language}/contact` },
    ]

    const toggleLanguage = (newLang: 'en' | 'fr' | 'ar') => {
        setLanguage(newLang);
    };

    const loginUrl = getAuthUrl(language, '/login');
    const signupUrl = getAuthUrl(language, '/signup');


    React.useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10) // Trigger slightly earlier
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return (
        <header dir={dir}>
            <nav
                data-state={menuState && 'active'}
                className={cn(
                    'fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300',
                    isScrolled ? 'bg-background/80 border-b border-border/40 backdrop-blur-md supports-[backdrop-filter]:bg-background/60' : 'bg-transparent border-transparent'
                )}>
                <div className="mx-auto max-w-7xl px-6">
                    <div className={cn('relative flex flex-wrap lg:flex-nowrap items-center justify-between gap-6 py-4 transition-all duration-200 lg:gap-0', isScrolled && 'py-3')}>

                        {/* Logo Area */}
                        <div className="flex w-full justify-between gap-6 lg:w-auto items-center">
                            <Logo />

                            {/* Mobile Toggle */}
                            <button
                                onClick={() => setMenuState(!menuState)}
                                aria-label={menuState == true ? 'Close Menu' : 'Open Menu'}
                                className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden text-foreground">
                                <Menu className={cn("m-auto size-6 duration-200", menuState ? "opacity-0 rotate-180 scale-0 absolute" : "opacity-100 rotate-0 scale-100")} />
                                <X className={cn("m-auto size-6 duration-200", menuState ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-180 scale-0 absolute")} />
                            </button>

                            {/* Desktop Menu */}
                            <div className="m-auto hidden size-fit lg:block">
                                <ul className="flex gap-1">
                                    {menuItems.map((item, index) => (
                                        <li key={index}>
                                            <Button
                                                asChild
                                                variant="ghost"
                                                size="sm"
                                                className="text-muted-foreground hover:text-foreground">
                                                {item.href.includes('/blog') ? (
                                                    <a href={item.href} className="text-sm font-medium">
                                                        <span>{item.name}</span>
                                                    </a>
                                                ) : (
                                                    <Link
                                                        href={item.href}
                                                        className="text-sm font-medium">
                                                        <span>{item.name}</span>
                                                    </Link>
                                                )}
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Actions Area (Desktop & Mobile Menu Content) */}
                        {/* Actions Area (Desktop & Mobile Menu Content) */}

                        {/* Desktop Actions (Hidden on Mobile) */}
                        <div className="hidden lg:flex lg:w-fit lg:gap-3 items-center">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground rounded-md">
                                        <Globe className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => toggleLanguage('en')}>English</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => toggleLanguage('fr')}>Français</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => toggleLanguage('ar')}>العربية</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <ModeToggle />

                            <Button
                                asChild
                                variant="ghost"
                                className="hidden h-9 px-4 lg:inline-flex rounded-md bg-background hover:bg-muted/50 dark:ring-input border-input/50 dark:border-input border-b-2 shadow-sm shadow-zinc-950/15 ring-1 ring-zinc-300">
                                <Link href={loginUrl}>
                                    <span>{t('login')}</span>
                                </Link>
                            </Button>
                            <Button
                                asChild
                                className="w-full h-9 px-4 lg:w-auto rounded-md from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5">
                                <Link href={signupUrl}>
                                    <span>{t('startNow')}</span>
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu Overlay (Full Width, positioned below nav) */}
                {menuState && (
                    <div className="absolute top-full left-0 right-0 z-50 w-full bg-background border-b border-border shadow-xl p-6 lg:hidden flex flex-col gap-6">
                        {/* Mobile Menu Links */}
                        <div className="w-full text-center">
                            <ul className="space-y-4 text-base">
                                {menuItems.map((item, index) => (
                                    <li key={index}>
                                        {item.href.includes('/blog') ? (
                                            <a
                                                href={item.href}
                                                className="block text-muted-foreground hover:text-foreground font-medium p-2 hover:bg-muted rounded-md transition-colors"
                                                onClick={() => setMenuState(false)}>
                                                <span>{item.name}</span>
                                            </a>
                                        ) : (
                                            <Link
                                                href={item.href}
                                                className="block text-muted-foreground hover:text-foreground font-medium p-2 hover:bg-muted rounded-md transition-colors"
                                                onClick={() => setMenuState(false)}>
                                                <span>{item.name}</span>
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <div className="my-4 h-px bg-border w-full" />
                        </div>

                        {/* Mobile Actions */}
                        <div className="flex w-full flex-col space-y-3 items-center">
                            {/* Language and Mode Toggle */}
                            <div className="flex items-center justify-center gap-2 mb-2 w-full">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground rounded-md">
                                            <Globe className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => toggleLanguage('en')}>English</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => toggleLanguage('fr')}>Français</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => toggleLanguage('ar')}>العربية</DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                <ModeToggle />
                            </div>

                            {/* Auth Buttons */}
                            <Button
                                asChild
                                variant="outline"
                                size="sm"
                                className="w-full h-10 rounded-md bg-background hover:bg-muted/50 dark:ring-input border-input/50 dark:border-input border-b-2 shadow-sm shadow-zinc-950/15 ring-1 ring-zinc-300">
                                <Link href={loginUrl}>
                                    <span>{t('login')}</span>
                                </Link>
                            </Button>
                            <Button
                                asChild
                                className="w-full h-10 px-4 rounded-md from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5">
                                <Link href={signupUrl}>
                                    <span>{t('startNow')}</span>
                                </Link>
                            </Button>
                        </div>
                    </div>
                )}
            </nav>
        </header >
    )
}
